import React, { useEffect, useRef, useState } from 'react';
import { Plus, Upload, Send, ShieldCheck, Edit2, Trash2, X, RefreshCw } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'pushed', label: 'Pushed' },
  { value: 'verified', label: 'Verified' },
  { value: 'failed', label: 'Failed' },
];

const TYPE_OPTIONS = [
  { value: 301, label: '301 (Permanent)' },
  { value: 302, label: '302 (Temporary)' },
  { value: 307, label: '307 (Temporary, preserve method)' },
  { value: 308, label: '308 (Permanent, preserve method)' },
];

const LIMIT = 200;

async function safeJson(res) {
  const text = await res.text();
  try { return JSON.parse(text || '{}'); }
  catch { throw new Error(`Non-JSON response (${res.status}): ${(text || '').slice(0, 200)}`); }
}

function buildFullUrl(pathOrUrl, baseDomain) {
  const base = String(baseDomain || '').replace(/\/+$/, '');
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return `${base}${pathOrUrl.startsWith('/') ? '' : '/'}${pathOrUrl}`;
}

function statusClass(status) {
  if (status === 'verified') return 'ok';
  if (status === 'failed') return 'manual';
  if (status === 'pushed') return 'warn';
  return 'muted-pill';
}

export default function RedirectsTab({ siteId, siteDomain }) {
  const [redirects, setRedirects] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [modal, setModal] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ source: '', target: '', redirect_type: 301 });
  const [csvText, setCsvText] = useState('');
  const [busy, setBusy] = useState(null);
  const [actionResult, setActionResult] = useState(null);
  const [message, setMessage] = useState('');
  const selectAllRef = useRef(null);

  useEffect(() => {
    setOffset(0);
    setSelected(new Set());
    loadRedirects(true);
  }, [siteId, statusFilter]);

  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      setSelected(new Set());
      loadRedirects(true);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const visibleIds = redirects.map(r => r.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someSelected = visibleIds.some(id => selected.has(id));
  const hasMore = redirects.length < total;

  useEffect(() => {
    if (!selectAllRef.current) return;
    selectAllRef.current.indeterminate = someSelected && !allSelected;
  }, [someSelected, allSelected]);

  async function loadRedirects(reset = true) {
    if (reset) setLoading(true);
    else setLoadingMore(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search) params.set('search', search);
      params.set('limit', String(LIMIT));
      params.set('offset', String(reset ? 0 : offset));
      const res = await fetch(`/api/sites/${siteId}/redirects?${params}`);
      const data = await safeJson(res);
      if (res.ok) {
        const newRules = data.redirects || [];
        if (reset) {
          setRedirects(newRules);
          setOffset(newRules.length);
        } else {
          setRedirects(prev => [...prev, ...newRules]);
          setOffset(prev => prev + newRules.length);
        }
        setTotal(data.total || 0);
        setCounts(data.counts || {});
      } else {
        setMessage(data.error || 'Failed to load redirects');
      }
    } catch (e) {
      setMessage(`Load failed: ${e.message}`);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }

  async function loadMore() {
    await loadRedirects(false);
  }

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Set());
  }

  function openCreate() {
    setEditing(null);
    setForm({ source: '', target: '', redirect_type: 301 });
    setActionResult(null);
    setMessage('');
    setModal('create');
  }

  function openEdit(rule) {
    setEditing(rule);
    setForm({ source: rule.source, target: rule.target, redirect_type: Number(rule.redirect_type) || 301 });
    setActionResult(null);
    setMessage('');
    setModal('edit');
  }

  function openCsv() {
    setCsvText('');
    setActionResult(null);
    setMessage('');
    setModal('csv');
  }

  async function handleSave() {
    setBusy('save');
    setMessage('');
    try {
      const url = editing
        ? `/api/sites/${siteId}/redirects/${editing.id}`
        : `/api/sites/${siteId}/redirects`;
      const method = editing ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await safeJson(res);
      if (!res.ok) { setMessage(data.error || 'Save failed'); return; }
      setMessage(editing
        ? 'Redirect updated. Status reset to pending; push to deploy.'
        : `Created ${data.created_count || 1} redirect${data.created_count === 1 ? '' : 's'}${data.duplicate_count ? ` · ${data.duplicate_count} duplicate(s) skipped` : ''}.`);
      setModal(null);
      await loadRedirects();
    } catch (e) {
      setMessage(`Save failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }

  async function handleDelete(rule) {
    if (!window.confirm(`Delete redirect from "${rule.source}" to "${rule.target}"?`)) return;
    setBusy('delete');
    setMessage('');
    try {
      const res = await fetch(`/api/sites/${siteId}/redirects/${rule.id}`, { method: 'DELETE' });
      const data = await safeJson(res);
      if (!res.ok) { setMessage(data.error || 'Delete failed'); return; }
      setMessage('Redirect deleted.');
      await loadRedirects();
    } catch (e) {
      setMessage(`Delete failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }

  async function handlePush() {
    const usingSelection = selected.size > 0;
    const ruleIds = usingSelection ? Array.from(selected) : null;
    const willPush = usingSelection ? ruleIds.length : (counts.pending || 0) + (counts.failed || 0);
    if (!willPush) { setMessage('Nothing to push.'); return; }
    const confirmMsg = usingSelection
      ? `Push ${willPush} selected redirect(s) to .htaccess? (Only pending/failed among them will be pushed.)`
      : `Push ${willPush} pending/failed redirect(s) to .htaccess?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy('push');
    setActionResult(null);
    setMessage('');
    try {
      const body = { statuses: ['pending', 'failed'] };
      if (ruleIds) body.rule_ids = ruleIds;
      const res = await fetch(`/api/sites/${siteId}/redirects/push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await safeJson(res);
      setActionResult({ type: 'push', usingSelection, ...data });
      if (usingSelection) clearSelection();
      await loadRedirects(true);
    } catch (e) {
      setActionResult({ type: 'push', ok: false, usingSelection, error: e.message });
    } finally {
      setBusy(null);
    }
  }

  async function handleVerify() {
    const usingSelection = selected.size > 0;
    const ruleIds = usingSelection ? Array.from(selected) : null;
    const willVerify = usingSelection ? ruleIds.length : (counts.pushed || 0);
    if (!willVerify) { setMessage('Nothing to verify.'); return; }
    const confirmMsg = usingSelection
      ? `HEAD-check ${willVerify} selected redirect(s) and update their status? (Only pushed among them will be verified.)`
      : `HEAD-check ${willVerify} pushed redirect(s) and update their status?`;
    if (!window.confirm(confirmMsg)) return;
    setBusy('verify');
    setActionResult(null);
    setMessage('');
    try {
      const body = { statuses: ['pushed'] };
      if (ruleIds) body.rule_ids = ruleIds;
      const res = await fetch(`/api/sites/${siteId}/redirects/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await safeJson(res);
      setActionResult({ type: 'verify', usingSelection, ...data });
      if (usingSelection) clearSelection();
      await loadRedirects(true);
    } catch (e) {
      setActionResult({ type: 'verify', ok: false, usingSelection, error: e.message });
    } finally {
      setBusy(null);
    }
  }

  async function handleCsvImport() {
    if (!csvText.trim()) { setMessage('Paste CSV content first.'); return; }
    setBusy('csv');
    setMessage('');
    try {
      const res = await fetch(`/api/sites/${siteId}/redirects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv: csvText, created_from: 'csv' }),
      });
      const data = await safeJson(res);
      if (!res.ok) { setMessage(data.error || 'Import failed'); return; }
      setMessage(`Imported ${data.created_count} redirect(s) from ${data.parsed_rows} parsed row(s)${data.duplicate_count ? ` · ${data.duplicate_count} duplicate(s) skipped` : ''}.`);
      setCsvText('');
      setModal(null);
      await loadRedirects();
    } catch (e) {
      setMessage(`Import failed: ${e.message}`);
    } finally {
      setBusy(null);
    }
  }

  return <section className="stack">
    <div className="card redirects-toolbar">
      <div>
        <h2><Send size={20} /> Redirect Manager</h2>
        <p className="muted">
          {total > 0 ? <>{total} rule{total === 1 ? '' : 's'} total
            {counts.pending ? <> · <strong>{counts.pending}</strong> pending</> : null}
            {counts.pushed ? <> · <strong>{counts.pushed}</strong> pushed</> : null}
            {counts.verified ? <> · <strong className="status-ok">{counts.verified}</strong> verified</> : null}
            {counts.failed ? <> · <strong className="status-bad">{counts.failed}</strong> failed</> : null}
          </> : 'No redirect rules yet. Add one or import a CSV to get started.'}
        </p>
      </div>
      <div className="redirects-actions">
        <button type="button" onClick={openCreate} disabled={!!busy}><Plus size={16} /> Add</button>
        <button type="button" onClick={openCsv} disabled={!!busy} className="secondary"><Upload size={16} /> Import CSV</button>
        <button type="button" onClick={handlePush} disabled={!!busy || (!(counts.pending || 0) && !(counts.failed || 0))}>
          {busy === 'push' ? 'Pushing...' : <><Send size={16} /> Push to .htaccess</>}
        </button>
        <button type="button" onClick={handleVerify} disabled={!!busy || !counts.pushed} className="secondary">
          {busy === 'verify' ? 'Verifying...' : <><ShieldCheck size={16} /> Verify</>}
        </button>
        <button type="button" onClick={loadRedirects} disabled={!!busy || loading} className="secondary" title="Reload">
          <RefreshCw size={16} className={loading ? 'spin' : ''} />
        </button>
      </div>
    </div>

    <div className="card redirects-filters">
      <input
        type="text"
        placeholder="Search source or target (e.g. /old-page)"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="redirects-search"
      />
      <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
        {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>

    {message && <div className="notice">{message}</div>}

    {actionResult && <div className={`notice ${actionResult.ok ? 'success' : 'error'}`}>
      {actionResult.type === 'push' && (actionResult.ok
        ? <><strong>Push complete{actionResult.usingSelection ? ' (selected)' : ''}:</strong> {actionResult.pushed} pushed, {actionResult.failed} failed
          {actionResult.batches ? <> across {actionResult.batches} batch(es)</> : null}.
          {actionResult.unsupported_types?.length ? <> {actionResult.unsupported_types.length} non-301 rule(s) skipped (bridge only supports 301).</> : null}
        </>
        : <><strong>Push failed:</strong> {actionResult.error || 'Unknown error'}</>
      )}
      {actionResult.type === 'verify' && (actionResult.ok
        ? <><strong>Verify complete{actionResult.usingSelection ? ' (selected)' : ''}:</strong> {actionResult.verified} verified, {actionResult.failed} failed of {actionResult.total} checked.</>
        : <><strong>Verify failed:</strong> {actionResult.error || 'Unknown error'}</>
      )}
    </div>}

    {selected.size > 0 ? <div className="card selection-bar">
      <span><strong>{selected.size}</strong> selected</span>
      <span className="muted">Push and Verify will act on the selection only.</span>
      <button type="button" onClick={clearSelection} className="secondary">Clear selection</button>
    </div> : null}

    <div className="card">
      {loading && redirects.length === 0 ? <p className="muted">Loading redirects...</p> : null}
      {!loading && redirects.length === 0 ? <p className="muted">
        {search || statusFilter ? 'No redirects match your filters.' : 'No redirect rules yet. Click "Add" or "Import CSV" to get started.'}
      </p> : null}
      {redirects.length > 0 ? <div className="responsive-table"><table>
        <thead>
          <tr>
            <th className="checkbox-cell">
              <input
                ref={selectAllRef}
                type="checkbox"
                checked={allSelected}
                onChange={toggleSelectAll}
                disabled={redirects.length === 0}
                title="Select all visible rows"
                aria-label="Select all visible rows"
              />
            </th>
            <th>Source</th>
            <th>Target</th>
            <th>Type</th>
            <th>Status</th>
            <th>Verified</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {redirects.map(r => <tr key={r.id} className={selected.has(r.id) ? 'selected-row' : ''}>
            <td className="checkbox-cell">
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggleSelect(r.id)}
                disabled={!!busy}
                aria-label={`Select redirect ${r.source}`}
              />
            </td>
            <td><a href={buildFullUrl(r.source, siteDomain)} target="_blank" rel="noreferrer"><code>{r.source}</code></a></td>
            <td><a href={buildFullUrl(r.target, siteDomain)} target="_blank" rel="noreferrer"><code>{r.target}</code></a></td>
            <td><span className="redirect-type">{r.redirect_type}</span></td>
            <td><span className={`pill ${statusClass(r.status)}`}>{r.status}</span></td>
            <td>{r.verified_at ? new Date(r.verified_at).toLocaleString() : '-'}</td>
            <td className="redirect-row-actions">
              <button type="button" onClick={() => openEdit(r)} disabled={!!busy} className="button-icon" title="Edit">
                <Edit2 size={14} />
              </button>
              <button type="button" onClick={() => handleDelete(r)} disabled={!!busy} className="button-icon danger" title="Delete">
                <Trash2 size={14} />
              </button>
            </td>
          </tr>)}
        </tbody>
      </table></div> : null}

      <div className="pagination-bar">
        <span className="muted">Showing {redirects.length} of {total}</span>
        <div className="pagination-actions">
          {hasMore ? <button type="button" onClick={loadMore} disabled={loadingMore || loading}>
            {loadingMore ? 'Loading...' : `Load ${Math.min(LIMIT, total - redirects.length)} more`}
          </button> : null}
          {!hasMore && total > LIMIT ? <span className="muted">All loaded</span> : null}
        </div>
      </div>
    </div>

    {(modal === 'create' || modal === 'edit') ? <RedirectFormModal
      title={modal === 'edit' ? 'Edit Redirect' : 'Add Redirect'}
      form={form}
      setForm={setForm}
      onSave={handleSave}
      onClose={() => setModal(null)}
      busy={busy === 'save'}
    /> : null}

    {modal === 'csv' ? <CsvImportModal
      csvText={csvText}
      setCsvText={setCsvText}
      onImport={handleCsvImport}
      onClose={() => { setModal(null); setCsvText(''); }}
      busy={busy === 'csv'}
    /> : null}
  </section>;
}

function RedirectFormModal({ title, form, setForm, onSave, onClose, busy }) {
  return <div className="audit-modal-backdrop" onClick={onClose}>
    <div className="audit-modal redirect-modal" onClick={e => e.stopPropagation()}>
      <div className="audit-progress-head">
        <strong>{title}</strong>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
      </div>
      <div className="form">
        <label>Source path or URL</label>
        <input
          type="text"
          value={form.source}
          onChange={e => setForm({ ...form, source: e.target.value })}
          placeholder="/old-page or https://old.example.com/page"
          autoFocus
        />
        <label>Target path or URL</label>
        <input
          type="text"
          value={form.target}
          onChange={e => setForm({ ...form, target: e.target.value })}
          placeholder="/new-page or https://example.com/page"
        />
        <label>Redirect type</label>
        <select value={form.redirect_type} onChange={e => setForm({ ...form, redirect_type: Number(e.target.value) })}>
          {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <p className="muted tiny">Editing a rule resets its status to <em>pending</em>. Run <strong>Push to .htaccess</strong> to deploy.</p>
        <div className="form-actions">
          <button type="button" onClick={onClose} className="secondary" disabled={busy}>Cancel</button>
          <button type="button" onClick={onSave} disabled={busy || !form.source || !form.target}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  </div>;
}

function CsvImportModal({ csvText, setCsvText, onImport, onClose, busy }) {
  return <div className="audit-modal-backdrop" onClick={onClose}>
    <div className="audit-modal csv-modal" onClick={e => e.stopPropagation()}>
      <div className="audit-progress-head">
        <strong>Import Redirects from CSV</strong>
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close"><X size={16} /></button>
      </div>
      <p className="muted">
        Format: <code>source,target,type</code> (header row optional). Type must be 301, 302, 307, or 308.
        Duplicate sources are skipped. Invalid rows are silently ignored.
      </p>
      <textarea
        value={csvText}
        onChange={e => setCsvText(e.target.value)}
        placeholder={`source,target,type\n/old-page/,/new-page/,301\n/shop/,https://example.com/,301`}
        rows={10}
        className="csv-textarea"
      />
      <div className="form-actions">
        <button type="button" onClick={onClose} className="secondary" disabled={busy}>Cancel</button>
        <button type="button" onClick={onImport} disabled={busy || !csvText.trim()}>
          {busy ? 'Importing...' : 'Import'}
        </button>
      </div>
    </div>
  </div>;
}
